import { useState, useCallback } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { EmailStep } from "./email-step";
import { CodeStep } from "./code-step";
import { useRequestCode, useVerifyCode } from "@/lib/api/free-auth";
import { useAuthStore } from "@/lib/stores/auth";
import { extractErrorMessage } from "@/lib/utils";
import { SparklesIcon } from "lucide-react";

type FlowStep = "email" | "code";

interface FreeLoginFlowProps {
  assessmentId: number;
  onSuccess: () => void;
}

export function FreeLoginFlow({ assessmentId, onSuccess }: FreeLoginFlowProps) {
  // Note: We don't fetch assessment details here since the user isn't authenticated yet
  // The generic "Assessment Login" header is shown instead
  void assessmentId; // Used for future enhancement if we add a public assessment info endpoint
  const [step, setStep] = useState<FlowStep>("email");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const requestCode = useRequestCode();
  const verifyCode = useVerifyCode();
  const fetchUser = useAuthStore((s) => s.fetchUser);

  const handleEmailSubmit = useCallback(
    async (submittedEmail: string) => {
      setError(null);
      try {
        await requestCode.mutateAsync({ email: submittedEmail });
        setEmail(submittedEmail);
        setStep("code");
      } catch (err) {
        setError(extractErrorMessage(err, "Failed to send code"));
      }
    },
    [requestCode]
  );

  const handleVerify = useCallback(
    async (code: string) => {
      setError(null);
      try {
        await verifyCode.mutateAsync({ email, code });
        // Refresh user state after successful verification
        await fetchUser();
        onSuccess();
      } catch (err) {
        setError(extractErrorMessage(err, "Invalid or expired code"));
      }
    },
    [verifyCode, email, fetchUser, onSuccess]
  );

  const handleResend = useCallback(async () => {
    setError(null);
    try {
      await requestCode.mutateAsync({ email });
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to resend code"));
    }
  }, [requestCode, email]);

  const handleBack = useCallback(() => {
    setStep("email");
    setError(null);
  }, []);

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Decorative background element */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-accent/30 rounded-full blur-3xl" />
      </div>

      <Card className="relative overflow-hidden border-border/50 shadow-lg">
        {/* Subtle top accent line */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <CardHeader className="text-center pb-2 space-y-4">
          {/* Free badge */}
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
              <SparklesIcon className="size-3" />
              Free Access
            </span>
          </div>

          <div className="space-y-2">
            <CardTitle className="text-xl font-semibold tracking-tight">
              Sign in to continue
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              Enter your email to access this assessment
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 pt-2">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2">
            <div
              className={`
                size-2 rounded-full transition-all duration-300
                ${step === "email" ? "bg-primary w-6" : "bg-primary/30"}
              `}
            />
            <div
              className={`
                size-2 rounded-full transition-all duration-300
                ${step === "code" ? "bg-primary w-6" : "bg-primary/30"}
              `}
            />
          </div>

          {/* Step content */}
          <div className="min-h-[280px]">
            {step === "email" ? (
              <EmailStep
                onSubmit={handleEmailSubmit}
                isLoading={requestCode.isPending}
                error={error}
              />
            ) : (
              <CodeStep
                email={email}
                onVerify={handleVerify}
                onBack={handleBack}
                onResend={handleResend}
                isVerifying={verifyCode.isPending}
                isResending={requestCode.isPending}
                error={error}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
